import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiErrorMessage, launchApi } from './api';
import ActivityWorkList, { defaultWorkListFilters, WorkListFilters, workListFilterKey } from './ActivityWorkList';
import { makeId, readStoredJson, today } from './helpers';
import { fieldHelp, help, HelpText, lifecycleHelp } from './helpTexts';
import LaunchFiles from './LaunchFiles';
import LaunchHistory from './LaunchHistory';
import LaunchOverview, { isReadOnlyLaunch, LaunchHeader, LifecycleBanner, overdueActivationWarning } from './LaunchOverview';
import { launchColors, overlineSx, sectionTitleSx } from './launchTheme';
import InfoTip from './ui/InfoTip';
import TimelineView from './TimelineView';
import { Assignee, InternalNotification, LaunchProduct, LaunchStatus, Workspace } from './types';

type WorkspaceTab = 'overview' | 'work' | 'timeline' | 'files' | 'history';

interface Props {
  launchId: string;
  currentUserId: string;
  canWrite: boolean;
  canAdminAll: boolean;
  assignees: Assignee[];
  myDepartments?: string[];
  notifications: InternalNotification[];
  refreshToken: number;
  activationSkipped?: boolean;
  onBack: () => void;
  onOpenActivity: (activityId: string) => void;
  onChanged: () => void;
}

const WorkspaceView: React.FC<Props> = ({
  launchId,
  currentUserId,
  canWrite,
  canAdminAll,
  assignees,
  myDepartments = [],
  notifications,
  refreshToken,
  activationSkipped = false,
  onBack,
  onOpenActivity,
  onChanged,
}) => {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [tab, setTab] = useState<WorkspaceTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<WorkListFilters>(() => readStoredJson(workListFilterKey, defaultWorkListFilters));
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState<string | null>(null);
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setWorkspace(await launchApi.workspace(launchId));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Lanseringens arbetsyta kunde inte hämtas.'));
    } finally {
      setLoading(false);
    }
  }, [launchId]);

  useEffect(() => { void load(); }, [load, refreshToken]);
  useEffect(() => {
    try { localStorage.setItem(workListFilterKey, JSON.stringify(filters)); } catch { /* lokala filter är frivilliga */ }
  }, [filters]);

  const conflict = async () => {
    setNotice('Någon annan hann ändra lanseringen. Arbetsytan har laddats om.');
    await load();
    onChanged();
  };

  const runLaunchCommand = async (command: Record<string, unknown>, success: string) => {
    if (!workspace) return;
    setBusy(true);
    setError(null);
    try {
      await launchApi.launchCommand(launchId, {
        commandId: makeId(String(command.type || 'launch')),
        expectedVersion: workspace.expectedVersion,
        actuallyOccurredAt: today(),
        ...command,
      });
      setLifecycleAction(null);
      setLifecycleReason('');
      setNotice(success);
      await load();
      onChanged();
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) await conflict();
      else setError(apiErrorMessage(requestError, 'Ändringen kunde inte sparas.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !workspace) return <Typography sx={{ p: 2 }}>Laddar lansering…</Typography>;
  if (error && !workspace) return <Alert severity="error" action={<Button onClick={onBack}>Till listan</Button>}>{error}</Alert>;
  if (!workspace) return null;

  const isOwner = canAdminAll || workspace.launch.ownerUserId === currentUserId;
  const canAdminister = isOwner && canWrite;
  const readOnly = isReadOnlyLaunch(workspace);
  const launchNotifications = notifications.filter((item) => item.launchId === launchId);
  const ownerName = assignees.find((person) => person.id === workspace.launch.ownerUserId)?.name || (workspace.launch.ownerUserId ? 'Okänd person' : 'Inte utsedd');
  const lifecycleActions = actionsForStatus(workspace.launch.status);
  const overdue = workspace.overview.scheduleCounts.overdue;
  const reasonRequired = Boolean(lifecycleAction && ['pause', 'close', 'cancel'].includes(lifecycleAction));
  const showWork = (patch: Partial<WorkListFilters>) => {
    setFilters((current) => ({ ...defaultWorkListFilters, sort: current.sort, group: current.group, ...patch }));
    setTab('work');
  };

  return (
    <Stack spacing={2}>
      <LaunchHeader
        workspace={workspace}
        ownerName={ownerName}
        canAdminister={canAdminister}
        lifecycleActions={lifecycleActions}
        onBack={onBack}
        onEdit={() => setMetadataOpen(true)}
        onLifecycle={setLifecycleAction}
      />
      <LifecycleBanner workspace={workspace} activationSkipped={activationSkipped} />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ borderBottom: `1px solid ${launchColors.divider}` }}>
        <Tabs value={tab} onChange={(_event, value: WorkspaceTab) => setTab(value)} variant="scrollable" allowScrollButtonsMobile aria-label="Lanseringens arbetsyta" sx={{ flex: 1, minWidth: 0 }}>
          <Tab value="overview" label="Översikt" />
          <Tab value="work" label="Arbetslista" />
          <Tab value="timeline" label="Tidslinje" />
          <Tab value="files" label="Filer" />
          <Tab value="history" label="Historik" />
        </Tabs>
        <InfoTip text={help.headerTabs} />
      </Stack>

      {tab === 'overview' && (
        <LaunchOverview
          workspace={workspace}
          isOwner={canAdminister}
          busy={busy}
          onOpenActivity={onOpenActivity}
          onLateChange={(command) => runLaunchCommand(command, 'Den sena ändringen har registrerats.')}
          onShowWork={showWork}
        />
      )}
      {tab === 'work' && (
        <ActivityWorkList
          workspace={workspace}
          assignees={assignees}
          currentUserId={currentUserId}
          myDepartments={myDepartments}
          filters={filters}
          setFilters={setFilters}
          onOpenActivity={onOpenActivity}
        />
      )}
      {tab === 'timeline' && (
        <TimelineView
          timeline={workspace.timeline}
          activities={workspace.activities}
          currentUserId={currentUserId}
          myDepartments={myDepartments}
          onOpenActivity={onOpenActivity}
        />
      )}
      {tab === 'files' && (
        <LaunchFiles
          launchId={launchId}
          files={workspace.files ?? { folders: [], attachments: [], canUpload: false, canManageFolders: false }}
          assignees={assignees}
          onOpenActivity={onOpenActivity}
          onChanged={() => { void load(); onChanged(); }}
          onError={setError}
          onNotice={setNotice}
        />
      )}
      {tab === 'history' && (
        <LaunchHistory
          workspace={workspace}
          notifications={launchNotifications}
          isOwner={canAdminister && !readOnly}
          busy={busy}
          onSnapshot={(command) => runLaunchCommand(
            command,
            command.type === 'commitPlanChange' ? 'Den nya grundplanen har fastställts.' : 'Veckoobservationen har sparats.',
          )}
          onOpenActivity={onOpenActivity}
        />
      )}

      <MetadataDialog
        open={metadataOpen}
        workspace={workspace}
        assignees={assignees}
        busy={busy}
        onClose={() => setMetadataOpen(false)}
        onSave={async (values) => {
          if (!workspace) return;
          setBusy(true);
          setError(null);
          try {
            const saved = await launchApi.launchCommand(launchId, {
              commandId: makeId('updateMetadata'),
              expectedVersion: workspace.expectedVersion,
              actuallyOccurredAt: today(),
              type: 'updateMetadata',
              name: values.name,
              description: values.description,
              ownerUserId: values.ownerUserId,
              products: values.products,
            });
            const currentRoles = Object.fromEntries((workspace.roles ?? []).filter((role) => role.userId).map((role) => [role.role, role.userId]));
            const nextRoles = Object.fromEntries(Object.entries(values.roleUsers).filter(([, userId]) => userId));
            let version = saved.expectedVersion;
            if (JSON.stringify(currentRoles) !== JSON.stringify(nextRoles)) {
              const savedRoles = await launchApi.launchCommand(launchId, {
                commandId: makeId('updateRoleUsers'),
                expectedVersion: version,
                actuallyOccurredAt: today(),
                type: 'updateRoleUsers',
                roleUsers: values.roleUsers,
              });
              version = savedRoles.expectedVersion;
            }
            if (values.chainChange) {
              await launchApi.launchCommand(launchId, {
                commandId: makeId('changeChains'),
                expectedVersion: version,
                actuallyOccurredAt: today(),
                type: 'changeChains',
                reason: values.chainChange.reason,
                add: values.chainChange.add,
                remove: values.chainChange.remove,
                move: values.chainChange.move,
              });
            }
            setMetadataOpen(false);
            setNotice('Lanseringens uppgifter har uppdaterats.');
            await load();
            onChanged();
          } catch (requestError: any) {
            if (requestError?.response?.status === 409) await conflict();
            else setError(apiErrorMessage(requestError, 'Ändringen kunde inte sparas.'));
          } finally {
            setBusy(false);
          }
        }}
      />
      <Dialog open={Boolean(lifecycleAction)} onClose={() => setLifecycleAction(null)} fullWidth maxWidth="xs">
        <DialogTitle>{lifecycleActions.find((item) => item.type === lifecycleAction)?.label || 'Ändra status'}</DialogTitle>
        <DialogContent>
          {lifecycleAction === 'activate' && overdue > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>{overdueActivationWarning(overdue)}</Alert>
          )}
          {reasonRequired
            ? <TextField autoFocus required fullWidth multiline minRows={2} sx={{ mt: 1 }} label="Orsak" value={lifecycleReason} onChange={(event) => setLifecycleReason(event.target.value)} />
            : <Typography>{lifecycleAction ? lifecycleHelp[lifecycleAction] : 'Bekräfta att lanseringens status ska ändras.'}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleAction(null)}>Avbryt</Button>
          <Button
            variant="contained"
            disabled={busy || (reasonRequired && !lifecycleReason.trim())}
            onClick={() => lifecycleAction && runLaunchCommand({ type: lifecycleAction, ...(lifecycleReason.trim() ? { reason: lifecycleReason.trim() } : {}) }, 'Lanseringens status har ändrats.')}
          >{lifecycleAction === 'activate' && overdue > 0 ? 'Aktivera ändå' : 'Bekräfta'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice || ''} />
    </Stack>
  );
};

const SectionHeading = ({ title, info }: { title: string; info: HelpText }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Typography component="h3" sx={sectionTitleSx}>{title}</Typography>
    <InfoTip text={info} />
  </Stack>
);

const MetadataDialog = ({
  open,
  workspace,
  assignees,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  workspace: Workspace;
  assignees: Assignee[];
  busy: boolean;
  onClose: () => void;
  onSave: (values: { name: string; description: string; ownerUserId: string; products: LaunchProduct[]; roleUsers: Record<string, string | null>; chainChange: ChainChangeDraft | null }) => void;
}) => {
  const [name, setName] = useState(workspace.launch.name);
  const [description, setDescription] = useState(workspace.launch.description);
  const [ownerUserId, setOwnerUserId] = useState(workspace.launch.ownerUserId || '');
  const [products, setProducts] = useState<Array<LaunchProduct & { articleNumber: string }>>(workspace.launch.products.map((product) => ({ ...product, articleNumber: product.articleNumber || '' })));
  const [roleUsers, setRoleUsers] = useState<Record<string, string>>({});
  const [chainRows, setChainRows] = useState<DraftChain[]>([]);
  const [chainOptions, setChainOptions] = useState<string[]>([]);
  const [newChainName, setNewChainName] = useState('');
  const [newChainDate, setNewChainDate] = useState(today());
  const [chainReason, setChainReason] = useState('');
  const [chainPreview, setChainPreview] = useState('');
  useEffect(() => {
    if (!open) return;
    setName(workspace.launch.name);
    setDescription(workspace.launch.description);
    setOwnerUserId(workspace.launch.ownerUserId || '');
    setProducts(workspace.launch.products.map((product) => ({ ...product, articleNumber: product.articleNumber || '' })));
    setRoleUsers(Object.fromEntries((workspace.roles ?? []).map((role) => [role.role, role.userId || ''])));
    setChainRows((workspace.chains ?? []).map((chain) => ({
      id: chain.id,
      name: chain.name,
      announcement: chain.announcement,
      originalAnnouncement: chain.announcement,
      categories: chain.calendarCategories ?? [],
      removed: false,
      added: false,
    })));
    setNewChainName('');
    setNewChainDate(today());
    setChainReason('');
    setChainPreview('');
    let cancelled = false;
    launchApi.settings()
      .then((settings) => { if (!cancelled) setChainOptions(settings.chains.filter((chain) => chain.active).map((chain) => chain.name)); })
      .catch(() => { if (!cancelled) setChainOptions([]); });
    return () => { cancelled = true; };
  }, [open, workspace]);
  const chainChange = useMemo(() => chainChangeFromRows(chainRows, chainReason), [chainReason, chainRows]);
  const reasonMissing = Boolean(chainChange && workspace.launch.status !== 'draft' && !chainReason.trim());
  useEffect(() => {
    if (!open || !chainChange || reasonMissing) {
      setChainPreview(reasonMissing ? 'Ange en orsak för att se vad som ändras.' : '');
      return undefined;
    }
    let cancelled = false;
    launchApi.launchCommand(workspace.launch.id, {
      commandId: 'preview-chains',
      expectedVersion: workspace.expectedVersion,
      actuallyOccurredAt: today(),
      type: 'changeChains',
      dryRun: true,
      reason: chainChange.reason,
      add: chainChange.add,
      remove: chainChange.remove,
      move: chainChange.move,
    }).then((result) => {
      const summary = (result as { summary?: { text?: string } }).summary;
      if (!cancelled) setChainPreview(summary?.text || '');
    }).catch((requestError) => {
      if (!cancelled) setChainPreview(apiErrorMessage(requestError, 'Förhandsvisningen kunde inte hämtas.'));
    });
    return () => { cancelled = true; };
  }, [chainChange, open, reasonMissing, workspace.expectedVersion, workspace.launch.id]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Redigera lansering</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }} divider={<Divider />}>
          <Stack spacing={1.5}>
            <SectionHeading title="Lansering" info={help.editLaunch} />
            <TextField required label="Namn" value={name} onChange={(event) => setName(event.target.value)} />
            <TextField label="Beskrivning" multiline minRows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
            <TextField required select label="Lanseringsägare" helperText={fieldHelp.owner} value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
              {ownerUserId && !assignees.some((person) => person.id === ownerUserId) && <MenuItem value={ownerUserId}>{ownerUserId}</MenuItem>}
              {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack spacing={1.5}>
            <SectionHeading title="Produkter" info={help.editProducts} />
            {products.map((product, index) => (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} key={product.id} alignItems={{ sm: 'flex-start' }}>
                <TextField required sx={{ flex: 1 }} label={`Produkt ${index + 1}`} value={product.name} onChange={(event) => setProducts((rows) => rows.map((row) => row.id === product.id ? { ...row, name: event.target.value } : row))} />
                <TextField sx={{ width: { sm: 180 } }} label="Artikelnummer" helperText="Valfritt." value={product.articleNumber} onChange={(event) => setProducts((rows) => rows.map((row) => row.id === product.id ? { ...row, articleNumber: event.target.value } : row))} />
                <IconButton aria-label={`Ta bort produkt ${index + 1}`} onClick={() => setProducts((rows) => rows.filter((row) => row.id !== product.id))}><DeleteOutlineIcon /></IconButton>
              </Stack>
            ))}
            <Button startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} onClick={() => setProducts((rows) => [...rows, { id: makeId('product'), name: '', articleNumber: '' }])}>Lägg till produkt</Button>
          </Stack>
          {(workspace.roles ?? []).length > 0 && (
            <Stack spacing={1.5}>
              <SectionHeading title="Roller" info={help.editRoles} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {(workspace.roles ?? []).map((role) => {
                  const selected = roleUsers[role.role] || '';
                  const changed = (selected || null) !== role.userId;
                  const preview = selected && changed && role.reassignableCount > 0
                    ? `${role.reassignableCount} uppgifter får ny ansvarig`
                    : !selected && role.userId
                      ? 'Ansvariga behålls'
                      : '';
                  return (
                    <TextField
                      key={role.role}
                      select
                      label={role.role}
                      helperText={preview || undefined}
                      value={selected}
                      onChange={(event) => setRoleUsers((current) => ({ ...current, [role.role]: event.target.value }))}
                    >
                      <MenuItem value="">Ingen</MenuItem>
                      {selected && !assignees.some((person) => person.id === selected) && <MenuItem value={selected}>{selected}</MenuItem>}
                      {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
                    </TextField>
                  );
                })}
              </Box>
            </Stack>
          )}
          <Stack spacing={1.5}>
            <SectionHeading title="Kedjor" info={help.editChains} />
            {chainRows.map((chain) => (
              <Stack key={chain.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ opacity: chain.removed ? 0.55 : 1 }}>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography sx={{ fontWeight: 700, color: launchColors.navy }}>{chain.name}</Typography>
                  {chain.categories.length > 0 && <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{chain.categories.join(' · ')}</Typography>}
                  {chain.added && <Typography sx={overlineSx}>Ny</Typography>}
                </Box>
                <TextField type="date" label="Aviseringsdatum" value={chain.announcement} disabled={chain.removed} onChange={(event) => setChainRows((rows) => rows.map((row) => row.id === chain.id ? { ...row, announcement: event.target.value } : row))} InputLabelProps={{ shrink: true }} />
                <Button onClick={() => setChainRows((rows) => rows.map((row) => row.id === chain.id ? { ...row, removed: !row.removed } : row))} disabled={!chain.removed && chainRows.filter((row) => !row.removed).length <= 1}>
                  {chain.removed ? 'Ångra' : 'Ta bort'}
                </Button>
              </Stack>
            ))}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
              {chainOptions.length > 0 ? (
                <TextField select label="Ny kedja" value={newChainName} onChange={(event) => setNewChainName(event.target.value)}>
                  <MenuItem value="">Välj kedja</MenuItem>
                  {chainOptions.filter((option) => !chainRows.some((chain) => !chain.removed && chain.name.toLocaleLowerCase('sv') === option.toLocaleLowerCase('sv'))).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              ) : (
                <TextField label="Ny kedja" value={newChainName} onChange={(event) => setNewChainName(event.target.value)} />
              )}
              <TextField type="date" label="Aviseringsdatum för ny kedja" value={newChainDate} onChange={(event) => setNewChainDate(event.target.value)} InputLabelProps={{ shrink: true }} />
              <Button startIcon={<AddIcon />} onClick={() => {
                const nextName = newChainName.trim();
                if (!nextName || !newChainDate) return;
                setChainRows((rows) => [...rows, { id: newChainId(rows.map((row) => row.id)), name: nextName, announcement: newChainDate, originalAnnouncement: '', categories: [], removed: false, added: true }]);
                setNewChainName('');
              }}>Lägg till</Button>
            </Stack>
            {chainChange && (
              <TextField label="Orsak till kedjeändring" value={chainReason} onChange={(event) => setChainReason(event.target.value)} helperText={workspace.launch.status === 'draft' ? 'Valfri i utkast.' : 'Krävs för en aktiv, pausad eller lanserad lansering.'} />
            )}
            {chainPreview && (
              <Box sx={{ bgcolor: launchColors.softNavy, borderRadius: '8px', px: 1.5, py: 1 }}>
                <Typography>{chainPreview}</Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={busy || reasonMissing || !name.trim() || !ownerUserId || products.some((product) => !product.name.trim())} onClick={() => onSave({
          name: name.trim(),
          description: description.trim(),
          ownerUserId,
          products: products.map((product) => ({ id: product.id, name: product.name.trim(), ...(product.articleNumber.trim() ? { articleNumber: product.articleNumber.trim() } : {}) })),
          roleUsers: Object.fromEntries((workspace.roles ?? []).map((role) => [role.role, roleUsers[role.role] || null])),
          chainChange,
        })}>Spara</Button>
      </DialogActions>
    </Dialog>
  );
};

type DraftChain = {
  id: string;
  name: string;
  announcement: string;
  originalAnnouncement: string;
  categories: string[];
  removed: boolean;
  added: boolean;
};

type ChainChangeDraft = {
  reason: string;
  add: Array<{ id: string; chain: string; announcement: string }>;
  remove: string[];
  move: Array<{ id: string; announcement: string }>;
};

function chainChangeFromRows(rows: DraftChain[], reason: string): ChainChangeDraft | null {
  const add = rows.filter((row) => row.added && !row.removed).map((row) => ({ id: row.id, chain: row.name, announcement: row.announcement }));
  const remove = rows.filter((row) => !row.added && row.removed).map((row) => row.id);
  const move = rows.filter((row) => !row.added && !row.removed && row.announcement !== row.originalAnnouncement).map((row) => ({ id: row.id, announcement: row.announcement }));
  if (!add.length && !remove.length && !move.length) return null;
  return { reason: reason.trim(), add, remove, move };
}

function newChainId(used: string[]): string {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 8)
    : String(Date.now());
  let id = `chain-${suffix}`;
  let extra = 2;
  while (used.includes(id)) {
    id = `chain-${suffix}-${extra}`;
    extra += 1;
  }
  return id;
}

function actionsForStatus(status: LaunchStatus): Array<{ type: string; label: string }> {
  if (status === 'draft') return [{ type: 'activate', label: 'Aktivera' }, { type: 'cancel', label: 'Avbryt lansering' }];
  if (status === 'active') return [{ type: 'pause', label: 'Pausa' }, { type: 'markLaunched', label: 'Markera lanserad' }, { type: 'close', label: 'Avsluta' }, { type: 'cancel', label: 'Avbryt lansering' }];
  if (status === 'paused') return [{ type: 'resume', label: 'Återuppta' }, { type: 'close', label: 'Avsluta' }, { type: 'cancel', label: 'Avbryt lansering' }];
  if (status === 'launched') return [{ type: 'close', label: 'Avsluta' }, { type: 'cancel', label: 'Avbryt lansering' }];
  return [];
}

export default WorkspaceView;
